import { Box, Field, Grid, GridItem, Heading, Separator, Text } from '@chakra-ui/react';
// @ts-ignore: TODO upgrade yup to v3
import { yupResolver } from '@hookform/resolvers/yup';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useUserCache } from 'src/apollo/cache';
import FieldErrorMessage from 'src/components/input/FieldErrorMessage';
import FormExpander from 'src/components/form-expander/FormExpander';
import useFeatureFlag from 'src/components/feature-flags/useFeatureFlag';
import StripeProvider from 'src/components/stripe/StripeProvider';
import useGetUrlParam from 'src/hooks/useGetUrlParam';
import fonts from 'src/theme/fonts';
import colors from 'src/theme/colors';
import BasicFields from 'src/pages/signup/form/BasicFields';
import PromoCode from 'src/pages/signup/form/PromoCode';
import { FormValues, initialValues } from 'src/pages/signup/form/FormValues';
import OrderSummary from 'src/pages/signup/form/OrderSummary';
import LocationCheck from 'src/pages/signup/form/LocationCheck';
import Address from 'src/pages/signup/form/Address';
import VatNumber from 'src/pages/signup/form/VatNumber';
import CountrySelect from 'src/pages/signup/form/CountrySelect';
import PostalCode from 'src/pages/signup/form/PostalCode';
import SubmitButton from 'src/pages/signup/form/SubmitButton';
import ConsentCheckboxGroup from 'src/pages/signup/form/ConsentCheckboxGroup';
import PlanDropdown from 'src/pages/signup/form/PlanDropdown';
import getTrialExpiryDate from 'src/pages/signup/helpers/getTrialExpiryDate';
import useSubmitSignupForm from 'src/pages/signup/hooks/useSubmitSignupForm';
import signupFormSchema from 'src/pages/signup/form/signupFormSchema';
import { useSignupPageContentQuery } from 'src/generated/graphql';
import { Checkbox } from 'src/components/ui/checkbox';

function PaddedDivider() {
  return <Separator my="24px" />;
}

function SignupFormInner() {
  const user = useUserCache();
  const { submissionError, onSubmit, isSubmitting } = useSubmitSignupForm();
  const email = useGetUrlParam('email');
  const promoCode = useGetUrlParam('promo');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isOrderSummaryVisible, setIsOrderSummaryVisible] = useState(false);
  const hasLongerTrialFlag = useFeatureFlag('14-day-trial');
  const { data, error, loading } = useSignupPageContentQuery({ fetchPolicy: 'cache-and-network' });

  const mainTitle = data?.signupPageContent?.mainTitle;

  const methods = useForm<FormValues>({
    resolver: yupResolver(signupFormSchema),
    defaultValues: {
      ...initialValues,
      plan: undefined,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      acceptTerms: !!user, // if the user exists, we can safely assume they have already accepted terms.
      emailOptIn: user?.emailOptIn ?? initialValues.emailOptIn,
      email: user?.email ?? email,
      password: user?.email ? 'Pl&ceholderPassw0rd' : '', // dummy password to make the disabled password box have dots in
      usePromoCode: !!promoCode,
      promoCode,
    },
  });

  const discount = methods.watch('discount');
  const paymentRequired = discount.percentOff !== 100 || discount.duration !== 'forever';
  const selectedPlan = methods.watch('plan');
  const showPromoCode = methods.watch('usePromoCode');
  const showVatFields = methods.watch('showVatFields');
  const country = methods.watch('country');

  const showPostalCode = paymentRequired && !showVatFields && ['US', 'CA', 'GB'].includes(country);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit((...params) => onSubmit(...params, recaptchaRef))}>
        {!loading && (
          <Heading mb="28px" size="h2">
            {error || !mainTitle ? 'Start your 7 day free trial' : mainTitle}
          </Heading>
        )}
        <Grid templateColumns="repeat(2, 1fr)" columnGap="20px" rowGap={['12px', '18px', '24px']}>
          <BasicFields />
          <GridItem colSpan={2}>
            <PlanDropdown />
          </GridItem>
          {selectedPlan && paymentRequired && (
            <GridItem colSpan={2}>
              <Box px="30px" py="12px" borderRadius="12px" bg={colors.podcastShade100}>
                <Text>
                  <Text display="inline" fontFamily={fonts.bold}>
                    You&apos;ll pay nothing today.&nbsp;
                  </Text>
                  Your trial ends on {getTrialExpiryDate(discount, hasLongerTrialFlag, false)}.
                  We&apos;ll remind you before your card gets charged so you can cancel if
                  you&apos;d like.
                </Text>
              </Box>
            </GridItem>
          )}
        </Grid>
        {selectedPlan && <PaddedDivider />}
        <Grid
          templateColumns="repeat(2, 1fr)"
          columnGap="20px"
          rowGap={['12px', '18px', '24px']}
          mb="24px"
          display={selectedPlan ? 'grid' : 'none'}
        >
          <GridItem colSpan={1}>
            <Controller
              control={methods.control}
              name="usePromoCode"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  defaultChecked={!!promoCode}
                  checked={value}
                  onCheckedChange={({ checked }) => onChange(checked)}
                >
                  <Text>
                    Add promo code&nbsp;
                    <Text color={colors.podcastShade400} display="inline">
                      (optional)
                    </Text>
                  </Text>
                </Checkbox>
              )}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <Controller
              control={methods.control}
              name="showVatFields"
              render={({ field: { onChange, value } }) => (
                <Field.Root invalid={!!methods.formState.errors.showVatFields}>
                  <Checkbox
                    invalid={!!methods.formState.errors.showVatFields}
                    checked={value}
                    onCheckedChange={({ checked }) => {
                      onChange(checked);
                    }}
                  >
                    <Text>
                      Add VAT ID&nbsp;
                      <Text color={colors.podcastShade400} display="inline">
                        (optional)
                      </Text>
                    </Text>
                  </Checkbox>
                  <FieldErrorMessage>
                    {methods.formState.errors.showVatFields?.message}
                  </FieldErrorMessage>
                </Field.Root>
              )}
            />
          </GridItem>
          {showPromoCode && (
            <GridItem colSpan={2}>
              <PromoCode />
            </GridItem>
          )}
          {showVatFields && (
            <GridItem colSpan={2}>
              <VatNumber />
              <Address />
            </GridItem>
          )}
        </Grid>
        <LocationCheck />
        {paymentRequired && selectedPlan && (
          <>
            <Box minH="82px" mb="12px">
              <PaymentElement
                options={{
                  fields: {
                    billingDetails: { address: { country: 'never', postalCode: 'never' } },
                  },
                  terms: { card: 'never' },
                }}
                onChange={e => {
                  methods.setValue('paymentElementComplete', e.complete);
                  methods.trigger('paymentElementComplete');
                }}
              />
            </Box>
            <Field.Root
              invalid={
                !!methods.formState.errors.paymentElementComplete && methods.formState.isSubmitted
              }
            >
              <FieldErrorMessage>Please fill in all the payment details.</FieldErrorMessage>
            </Field.Root>
          </>
        )}
        {selectedPlan && (
          <Grid templateColumns="repeat(2, 1fr)" columnGap="20px" rowGap={['12px', '18px', '24px']}>
            <GridItem colSpan={2}>
              <CountrySelect />
            </GridItem>
            {showPostalCode && (
              <GridItem>
                <PostalCode country={country} />
              </GridItem>
            )}
            <GridItem
              colSpan={showPostalCode ? 1 : 2}
              pt={showPostalCode ? 'calc(1rem + 8px)' : ''}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FormExpander
                isExpanded={isOrderSummaryVisible}
                collapsedLabel="See subscription breakdown"
                expandedLabel="Hide breakdown"
                onClick={() => setIsOrderSummaryVisible(!isOrderSummaryVisible)}
              />
            </GridItem>
          </Grid>
        )}
        {isOrderSummaryVisible && (
          <Box mt="15px">
            <OrderSummary />
          </Box>
        )}
        <SubmitButton isSubmitting={isSubmitting} submissionError={submissionError} />
        {process.env.NODE_ENV !== 'development' && (
          // @ts-ignore: TODO: fix types for ReCAPTCHA
          <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_KEY} size="invisible" />
        )}
        <ConsentCheckboxGroup />
      </form>
    </FormProvider>
  );
}

export default function SignupForm() {
  return (
    <StripeProvider>
      <SignupFormInner />
    </StripeProvider>
  );
}
